// @flow
import React, {useState, useRef, useEffect} from 'react';
import {useLocation} from 'react-router-dom';
import {
  ButtonBase,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';

import NewMessageInput from '../NewMessageInput/NewMessageInput';
import EmojiPicker from '../../../../../../../../elements/EmojiPicker/EmojiPicker';
import {
  AttachFileOutlined,
  EmojiEmotionsOutlined,
  MicNone,
  Send,
  NoteAddOutlined,
} from '@material-ui/icons';

import {useForm, FormProvider} from 'react-hook-form';
import {
  AUDIO_SERVICE_AVAILABLE,
  MESSAGE_FORM_FIELDS,
  getMessageTypeFromMime,
} from '../../../constants';

import ChatLightningIcon from '../../../../../../../../elements/icons/ChatLightningIcon';
import {colors} from '../../../../../../../../assets/styles/colors';
import QuickRepliesSection from './QuickRepliesSection';
import {useToggle, useDebounce} from '../../../../../../../../utils/react';
import {replaceTextPattern} from '../../../../../../../../utils/string';
import {QUICK_REPLY_MESSAGE_INTERPOLATION} from '../../../../../../quick-replies/components/RepliesForm/constants';
import useOnConversationChange, {
  getConversationDisplayName,
  useCurrentConversation,
} from '../../../../../utils/conversation';
import AttachmentPreview from './AttachmentPreview';
import ImageIcon from '../../../../../../../../elements/icons/ImageIcon';

import type {MessageFormFields} from '../../../constants';
import type {Theme} from '@material-ui/core';
import type {Emoji} from '../../../../../../../../elements/EmojiPicker/EmojiPicker';
import {MESSAGE_TYPES} from '../../../../../../../../entities';
import {getClipboardFile} from '../../../../../../../../utils/dom';
import {getServiceGradient} from '../../../../../../constans';
import AudioRecorder from './AudioRecorder';
import {useRealTimeMultichat} from '../../../../../../real-time/utils';
import InlineLoader from '../../../../../../../../elements/LoadingSpinner/InlineLoader';
import {CONNECTION_STATUS} from '../../../../../../real-time/duck';
import {
  extractFileData,
  getLabelFromMimetype,
} from '../../../../../../../../utils/file';

import {getOriginalFileUrl} from '../../../../../../../file-storage/utils';
import {debounce} from '../../../../../../../../utils/functions';
import WhatsApiTemplateModal from './WhatsapiTemplateModal';
import {setChatMessageBehaviorService} from '../../../../../promises';

type Props = {
  onSendMessage: (params: MessageFormFields) => Promise<void>,
  disabled?: boolean,
  isReplying?: boolean,
};
const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    paddingInline: '.75rem',
    paddingBottom: '.75rem',
  },
  loader: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    cursor: 'wait',
    zIndex: 1,
    width: '100%',
    height: '100%',
  },
  connectingContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    cursor: 'wait',
    zIndex: 1,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(255,255,255,0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    gap: '.5rem',
    padding: '.5rem 1rem',
  },
  connectingText: {
    color: colors.grey900,
    fontSize: '1rem',
    fontWeight: 500,
    textAlign: 'center',
  },
  emojisWrap: {
    position: 'absolute',
    top: '-440px',
    left: 0,
  },
  messageOptions: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  connectionStateDot: {
    width: '.75rem',
    height: '.75rem',
    borderRadius: '50%',
    backgroundColor: colors.grey900,

    '&[data-status="CONNECTED"]': {
      backgroundColor: colors.success,
    },

    '&[data-status="DISCONNECTED"]': {
      backgroundColor: colors.error,
    },
  },
  rightContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
  },
  sendButton: {
    padding: '.5rem',
    color: 'white',
    backgroundColor: colors.primary,
    borderRadius: '50%',

    '&:hover': {
      color: 'white',
    },

    '& svg': {
      transform: 'translateX(2px)',
    },
  },
  quickReplyWrap: {
    position: 'absolute',
    bottom: '100px',
    left: 0,
    width: '100%',
    zIndex: 1,
  },
  filesAttachmentWrap: {
    order: -1,
  },

  buttonOptions: {
    display: 'flex',
    alignItems: 'center',
    gap: '.125rem',
  },
  labelButton: {
    padding: '.25rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'rgba(0, 0, 0, 0.54)',

    cursor: 'pointer',
    fontSize: '1.5rem',
    borderRadius: '50%',

    '&:hover': {
      color: colors.purple,
    },
  },
}));

const SHORTCUTS_KEYS = [
  {
    key: '/',
    label: 'Abrir respuestas rápidas',
    description: 'Abre el menú de respuestas rápidas',
    id: 'open-quick-replies',
  },
];

// helpers.ts
export function findLastShortcut(text: string) {
  let lastPos = -1;
  let lastShortcut = null;

  for (const shortcut of SHORTCUTS_KEYS) {
    const pos = text.lastIndexOf(shortcut.key);
    if (pos > lastPos) {
      lastPos = pos;
      lastShortcut = shortcut;
    }
  }

  return lastPos !== -1 ? {shortcut: lastShortcut, position: lastPos} : null;
}

const TYPING_DELAY_MS = 700;

export default function BottomContent(props: Props) {
  const {onSendMessage, disabled, isReplying} = props;

  const classes = useStyles();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  const messageValueFromParams = searchParams.get('message') || '';

  const {currentConversation} = useCurrentConversation();

  // const previousCurrentConversation = useSafePrevious(currentConversation);

  const {connecting, status} = useRealTimeMultichat();

  const [isSending, setIsSending] = useState(false);

  const [recordingActive, setRecordingActive] = useState(false);

  const methods = useForm<MessageFormFields>();
  const {getValues, setValue, register} = methods;

  const wordSelectionIndex = useRef(0);
  const typingTimeoutRef = useRef<Timeout | null>(null);

  const debouncedSendComposing = useRef(
    debounce(
      (conversationId, params) => {
        setChatMessageBehaviorService(conversationId, params).catch(
          console.error
        );
      },
      300,
      false
    )
  ).current;

  const [
    quickRepliesOpen,
    toggleQuickRepliesOpen,
    manualSetQuickReplyOpen,
  ] = useToggle();

  const [activeShortcut, setActiveShortcut] = useState(null);
  const [shortcutKeyword, setShortcutKeyword] = useState('');
  const [quickReplySearchKeyword, setQuickReplySearchKeyword] = useState('');

  const debouncedQuickReplySearchKeyword = useDebounce(
    quickReplySearchKeyword,
    500
  );

  const [openEmojis, setOpenEmojis] = useState<boolean>(false);

  const [openWhatsApiTemplateModal, toggleWhatsApiTemplateModal] = useToggle();

  const openEmojiPicker = () => setOpenEmojis(true);
  const closeEmojiPicker = () => setOpenEmojis(false);

  useEffect(() => {
    register(MESSAGE_FORM_FIELDS.files);
    register(MESSAGE_FORM_FIELDS.type);
    register(MESSAGE_FORM_FIELDS.attachment);
  }, [register]);

  useEffect(() => {
    if (!messageValueFromParams) return;

    const decodedMessage = decodeURIComponent(messageValueFromParams);
    setTimeout(() => {
      setValue(MESSAGE_FORM_FIELDS.message, decodedMessage);
    }, 1000);
  }, [messageValueFromParams, setValue]);

  /*  useEffect(() => {
    const isSameConversation =
      currentConversation?.id === previousCurrentConversation?.id;

    return () => {
      if (!isSameConversation && previousCurrentConversation) {
        setValue(MESSAGE_FORM_FIELDS.message, '');
        setValue(MESSAGE_FORM_FIELDS.files, undefined);
        setValue(MESSAGE_FORM_FIELDS.type, MESSAGE_TYPES.TEXT);
        setValue(MESSAGE_FORM_FIELDS.attachment, undefined);

        closeEmojiPicker();
        setRecordingActive(false);
      }
    };
  }, [currentConversation, previousCurrentConversation, setValue]); */

  useOnConversationChange(currentConversation, () => {
    setValue(MESSAGE_FORM_FIELDS.message, '');
    setValue(MESSAGE_FORM_FIELDS.files, undefined);
    setValue(MESSAGE_FORM_FIELDS.type, MESSAGE_TYPES.TEXT);
    setValue(MESSAGE_FORM_FIELDS.attachment, undefined);
    closeEmojiPicker();
    setRecordingActive(false);
  });

  const onEmojiPick = (emoji: Emoji) => {
    const message = getValues(MESSAGE_FORM_FIELDS.message);

    const newMessage = `${message.slice(0, wordSelectionIndex.current)}${
      emoji.native
    }${message.slice(wordSelectionIndex.current)}`;

    setValue(MESSAGE_FORM_FIELDS.message, newMessage);
  };

  const handleSelectionStart = e => {
    if (!e.target) return;
    wordSelectionIndex.current = e.target.selectionStart;
  };

  const resetForm = () => {
    setValue(MESSAGE_FORM_FIELDS.message, '');
    setValue(MESSAGE_FORM_FIELDS.files, undefined);
    setValue(MESSAGE_FORM_FIELDS.type, MESSAGE_TYPES.TEXT);
    setValue(MESSAGE_FORM_FIELDS.attachment, undefined);
  };

  const onSubmit = (data: MessageFormFields) => {
    if (isSending) return;
    setIsSending(true);

    // If message is sent, clear typing timeout and send PAUSED behavior
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    if (
      currentConversation &&
      currentConversation.ravenService === 'WHATSAPP_WEB'
    ) {
      const activeWhatsappDevice = currentConversation.deviceId;
      if (activeWhatsappDevice) {
        setChatMessageBehaviorService(currentConversation.id, {
          service: 'WHATSAPP_WEB',
          deviceId: activeWhatsappDevice,
          behavior: 'PAUSED',
        }).catch(console.error);
      }
    }

    onSendMessage(data)
      .then(() => {
        resetForm();
        methods.reset();
      })
      .finally(() => {
        setIsSending(false);
      });
  };

  const handleUploadFile = e => {
    const files = Array.from(e.target.files);
    const areVideoFiles = files.every(file => file.type.includes('video'));
    setValue(MESSAGE_FORM_FIELDS.files, files);
    setValue(
      MESSAGE_FORM_FIELDS.type,
      areVideoFiles ? MESSAGE_TYPES.VIDEO : MESSAGE_TYPES.DOCUMENT
    );
    manualSetQuickReplyOpen(false);
    e.target.value = null;
  };

  const handleUploadImage = e => {
    const files = Array.from(e.target.files);
    setValue(MESSAGE_FORM_FIELDS.files, files);
    setValue(MESSAGE_FORM_FIELDS.type, MESSAGE_TYPES.IMAGE);
    manualSetQuickReplyOpen(false);
    e.target.value = null;
  };

  const handlePasteFiles = async e => {
    const files = await getClipboardFile(e);

    if (Array.isArray(files) && files.length > 0) {
      setValue(MESSAGE_FORM_FIELDS.files, files);
      setValue(MESSAGE_FORM_FIELDS.type, MESSAGE_TYPES.IMAGE);
    } else {
      setValue(MESSAGE_FORM_FIELDS.type, MESSAGE_TYPES.TEXT);
    }
  };

  useEffect(() => {
    if (activeShortcut?.id === 'open-quick-replies') {
      setQuickReplySearchKeyword(shortcutKeyword);
    }
  }, [shortcutKeyword, activeShortcut]);

  if (recordingActive) {
    return (
      <AudioRecorder
        onClose={() => setRecordingActive(false)}
        onSendMessage={onSendMessage}
      />
    );
  }

  /*   const chatDisabled =
    disabled || isSending || connecting || status === 'DISCONNECTED';
 */

  const chatDisabled = false;

  const showAudioButton = AUDIO_SERVICE_AVAILABLE.includes(
    currentConversation && currentConversation.ravenService
  );

  const handleSelectReply = async reply => {
    if (!currentConversation) return;

    const {crmCustomer} = currentConversation;

    const clientName = crmCustomer
      ? crmCustomer.fullName
      : getConversationDisplayName(currentConversation);

    const regex = /{{([^}]+)}}/g;
    const data = {
      [QUICK_REPLY_MESSAGE_INTERPOLATION.clientName.key]: clientName,
    };
    const message = replaceTextPattern(regex, reply.message, data);
    setValue(MESSAGE_FORM_FIELDS.message, message);
    toggleQuickRepliesOpen();

    if (!reply.fileId || !reply.fileType) return;

    try {
      const fileUrl = await getOriginalFileUrl(reply.fileId);

      extractFileData(fileUrl).then(result => {
        const {type} = result;
        const name = getLabelFromMimetype(type);
        const messageType = getMessageTypeFromMime(type);

        setValue(MESSAGE_FORM_FIELDS.attachment, {
          type,
          name,
          src: fileUrl,
        });

        setValue(MESSAGE_FORM_FIELDS.type, messageType);
      });
    } catch (error) {
      console.error(error);
    }
  };

  const clearShortcut = () => {
    setActiveShortcut(null);
    setShortcutKeyword('');
    setQuickReplySearchKeyword('');
    manualSetQuickReplyOpen(false);
  };

  const handleInputKeyDown = e => {
    const key = e.key;

    if (e.key === 'Escape') {
      clearShortcut();
      return;
    }

    const shortCut = SHORTCUTS_KEYS.find(item => item.key === key);

    if (!shortCut) return;

    if (shortCut.id === 'open-quick-replies') {
      manualSetQuickReplyOpen(true);
    }
  };

  const handleInputChange = e => {
    const text = e.target.value;

    // Send COMPOSING behavior
    if (
      currentConversation &&
      currentConversation.ravenService === 'WHATSAPP_WEB'
    ) {
      const activeWhatsappDevice = currentConversation.deviceId;
      if (activeWhatsappDevice) {
        debouncedSendComposing(currentConversation.id, {
          service: 'WHATSAPP_WEB',
          deviceId: activeWhatsappDevice,
          behavior: 'COMPOSING',
        });

        // Clear existing timeout
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }

        // Set new timeout to send PAUSED behavior after 300ms of no typing
        typingTimeoutRef.current = setTimeout(() => {
          setChatMessageBehaviorService(currentConversation.id, {
            service: 'WHATSAPP_WEB',
            deviceId: activeWhatsappDevice,
            behavior: 'PAUSED',
          }).catch(console.error);
        }, TYPING_DELAY_MS);
      }
    }

    const result = findLastShortcut(text);

    if (!result) {
      clearShortcut();
      return;
    }

    const {shortcut, position} = result;
    const rest = text.slice(position + shortcut?.key?.length);

    if (/^\s/.test(rest) || rest.includes(' ')) {
      clearShortcut();
      return;
    }

    setActiveShortcut(shortcut);
    setShortcutKeyword(rest);
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        className={classes.root}
        onPaste={handlePasteFiles}
      >
        {isSending && <div className={classes.loader} />}

        {connecting && (
          <div className={classes.connectingContainer}>
            <Typography className={classes.connectingText}>
              Conectando con el servicio de mensajería...
            </Typography>
            <InlineLoader loading size={16} />
          </div>
        )}
        <NewMessageInput
          name={MESSAGE_FORM_FIELDS.message}
          onClick={handleSelectionStart}
          onKeyUp={handleSelectionStart}
          placeholder="Responder..."
          onSubmit={methods.handleSubmit(onSubmit)}
          disabled={chatDisabled}
          autoFocus={isReplying}
          onKeyDown={handleInputKeyDown}
          onChange={handleInputChange}
        />
        <div className={classes.messageOptions}>
          <div className={classes.buttonOptions}>
            <Tooltip title="Emoji" placement="top">
              <ButtonBase
                className={classes.labelButton}
                onClick={openEmojiPicker}
                disabled={chatDisabled}
              >
                <EmojiEmotionsOutlined fontSize="default" />
              </ButtonBase>
            </Tooltip>
            <Tooltip title="Subir imagen" placement="top">
              <ButtonBase
                className={classes.labelButton}
                component="label"
                disabled={chatDisabled}
              >
                <ImageIcon />
                <input
                  type="file"
                  hidden
                  onChange={handleUploadImage}
                  accept="image/*"
                />
              </ButtonBase>
            </Tooltip>

            <Tooltip title="Subir archivos" placement="top">
              <ButtonBase
                component="label"
                className={classes.labelButton}
                disabled={chatDisabled}
              >
                <AttachFileOutlined />
                <input type="file" hidden onChange={handleUploadFile} />
              </ButtonBase>
            </Tooltip>

            <Tooltip
              title="Respuestas rápidas"
              placement="top"
              disabled={chatDisabled}
            >
              <ButtonBase
                className={classes.labelButton}
                onClick={toggleQuickRepliesOpen}
                style={{
                  color: quickRepliesOpen ? colors.purple : undefined,
                }}
              >
                <ChatLightningIcon fontSize="small" />
              </ButtonBase>
            </Tooltip>

            {currentConversation?.ravenService === 'WHATSAPP_API' && (
              <Tooltip title="Usar template" placement="top">
                <ButtonBase
                  className={classes.labelButton}
                  onClick={toggleWhatsApiTemplateModal}
                  style={{
                    color: openWhatsApiTemplateModal
                      ? colors.purple
                      : undefined,
                  }}
                >
                  <NoteAddOutlined fontSize="small" />
                </ButtonBase>
              </Tooltip>
            )}

            {showAudioButton && (
              <Tooltip
                title="Grabar audio"
                placement="top"
                disabled={chatDisabled}
              >
                <ButtonBase
                  className={classes.labelButton}
                  onClick={() => setRecordingActive(true)}
                >
                  <MicNone />
                </ButtonBase>
              </Tooltip>
            )}
          </div>

          <div className={classes.rightContent}>
            <Tooltip
              title={
                status === CONNECTION_STATUS.CONNECTED
                  ? 'Conectado'
                  : 'Desconectado'
              }
              placement="top"
              disabled={chatDisabled}
            >
              <span
                className={classes.connectionStateDot}
                data-status={status}
              ></span>
            </Tooltip>

            <IconButton
              size="small"
              type="submit"
              color="primary"
              className={classes.sendButton}
              disabled={disabled || isSending}
              style={{
                background:
                  currentConversation &&
                  getServiceGradient(currentConversation.ravenService),
              }}
            >
              <Send fontSize="small" />
            </IconButton>
          </div>
        </div>
        <div className={classes.emojisWrap}>
          {openEmojis && (
            <EmojiPicker
              onEmojiPick={onEmojiPick}
              previewPosition="none"
              onClickOutside={closeEmojiPicker}
            />
          )}
        </div>
        {quickRepliesOpen && (
          <div className={classes.quickReplyWrap}>
            <QuickRepliesSection
              searchValue={debouncedQuickReplySearchKeyword}
              onSelectReply={reply => {
                handleSelectReply(reply);
              }}
            />
          </div>
        )}

        {openWhatsApiTemplateModal && (
          <WhatsApiTemplateModal
            onSubmitSuccess={toggleWhatsApiTemplateModal}
          />
        )}

        <div className={classes.filesAttachmentWrap}>
          <AttachmentPreview />
        </div>
      </form>
    </FormProvider>
  );
}
